import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	Paper,
	Skeleton,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LockIcon from '@mui/icons-material/Lock';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { postCashDeposit, previewCashDeposit, extractApiError, extractDepositValidationError } from '../../services';
import { useBranchReceiptsList } from '../../hooks/useBranchReceiptsList';
import { IBranchReceiptItem, ICashDepositPreview } from '../../types';
import { BRANCH_CURRENCIES, VAULT_BRANCHES } from '../../constants';
import { formatCashAmount } from '../BranchCashSummary';
import routes from 'constants/routes';

// ─── Tooltips ─────────────────────────────────────────────────────────────────

const equationTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Cómo funciona la ecuación? 🧮
		</Typography>
		<Typography variant="body2" gutterBottom>
			El efectivo en mano = recibos + vueltos que salieron de la caja chica (el cliente pagó de más y le diste cambio).
			Ese total se distribuye en:
		</Typography>
		<Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
			<li>
				<strong>Monto al banco (o caja fuerte)</strong> — lo que llevás en el sobre.
			</li>
			<li>
				<strong>Resto en caja chica</strong> — si llevás menos que el efectivo en mano, la diferencia queda en la chica.
			</li>
			<li>
				<strong>Retiro de caja chica</strong> — si llevás más que el efectivo en mano, la diferencia sale de la chica.
			</li>
		</Typography>
		<Typography variant="body2" mt={1}>
			Cada acta de depósito es por una sola moneda. Si tenés efectivo en varias monedas, hacé un depósito por cada una.
		</Typography>
	</>
);

const changeChecklistTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué son los vueltos a compensar? 🔄
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cuando un cliente paga de más y le das cambio de la caja chica, ese cambio queda registrado como un{' '}
			<strong>vuelto pendiente</strong> — y la plata de más queda dentro del sobre de cobranza.
		</Typography>
		<Typography variant="body2" gutterBottom>
			Al depositar, marcá cuáles de esos vueltos estás compensando con este sobre. Así el sistema sabe que ese dinero
			extra ya salió de la chica y <strong>no lo descuenta dos veces</strong>.
		</Typography>
		<Typography variant="body2">
			Si no marcás ninguno, el sobre debería tener exactamente el total de recibos.
		</Typography>
	</>
);

const selectReceiptsTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Cuáles recibos selecciono? 📋
		</Typography>
		<Typography variant="body2" gutterBottom>
			Solo se muestran los recibos <strong>libres</strong> — los que ya están en un borrador de acta no aparecen acá.
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cada depósito es por una moneda. Usá las solapas para ver y seleccionar los recibos en cada moneda por separado.
		</Typography>
		<Typography variant="body2">
			Lo más común es seleccionar todo de una sola vez, pero podés hacer depósitos parciales.
		</Typography>
	</>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
	return new Date(isoString).toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function totalInCurrency(receipts: IBranchReceiptItem[], currency: string): number {
	return receipts
		.filter((r) => r.transactionCurrency === currency)
		.reduce((sum, r) => sum + Number(r.transactionAmount), 0);
}

function round2(n: number): number {
	return Math.round(n * 100) / 100;
}

// ─── Paso 1: Selección de recibos ─────────────────────────────────────────────

interface Step1Result {
	receipts: IBranchReceiptItem[];
	currency: string;
}

interface Step1Props {
	branch: string;
	onNext: (result: Step1Result) => void;
}

function Step1SelectReceipts({ branch, onNext }: Step1Props): JSX.Element {
	const branchCurrencies = BRANCH_CURRENCIES[branch] ?? ['USD'];
	const [currency, setCurrency] = useState(branchCurrencies[0]);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	const { receipts, pagination, loading, loadingMore, error, loadMore, hasMore } = useBranchReceiptsList(branch, 50);

	// Recibos libres en la moneda activa. Si depositStatus es undefined (campo nuevo
	// todavía no en la API), tratamos todos como libres.
	const freeAll = useMemo(
		() => receipts.filter((r) => r.depositStatus === null || r.depositStatus === undefined),
		[receipts],
	);

	const freeByCurrency = useMemo(() => freeAll.filter((r) => r.transactionCurrency === currency), [freeAll, currency]);

	// Conteo por moneda para mostrar en las tabs
	const countPerCurrency = useMemo(() => {
		const acc: Record<string, number> = {};
		freeAll.forEach((r) => {
			acc[r.transactionCurrency] = (acc[r.transactionCurrency] ?? 0) + 1;
		});
		return acc;
	}, [freeAll]);

	const selectedInView = freeByCurrency.filter((r) => selectedIds.has(r.id));
	const allSelected = freeByCurrency.length > 0 && selectedInView.length === freeByCurrency.length;
	const total = selectedInView.reduce((sum, r) => sum + Number(r.transactionAmount), 0);

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				freeByCurrency.forEach((r) => next.delete(r.id));
			} else {
				freeByCurrency.forEach((r) => next.add(r.id));
			}
			return next;
		});
	};

	const handleCurrencyChange = (_: React.SyntheticEvent, newCurrency: string) => {
		setCurrency(newCurrency);
	};

	// Los recibos seleccionados que van al paso 2 son los de la moneda activa
	const handleContinuar = () => {
		const selected = freeByCurrency.filter((r) => selectedIds.has(r.id));
		onNext({ receipts: selected, currency });
	};

	const selectedCountInView = selectedInView.length;

	return (
		<>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
				<Box display="inline-flex" alignItems="center" gap={0.5}>
					<Typography variant="subtitle2">
						Recibos disponibles{pagination ? ` (${pagination.total} total)` : ''}
					</Typography>
					<SectionInfoTooltip content={selectReceiptsTooltip} />
				</Box>
				{freeByCurrency.length > 0 && (
					<Button size="small" variant="text" sx={{ textTransform: 'none' }} onClick={toggleAll}>
						{allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
					</Button>
				)}
			</Box>

			{/* Tabs por moneda — solo si hay más de una */}
			{branchCurrencies.length > 1 && (
				<Tabs
					value={currency}
					onChange={handleCurrencyChange}
					variant="scrollable"
					sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
				>
					{branchCurrencies.map((c) => (
						<Tab
							key={c}
							value={c}
							label={
								<Box display="flex" alignItems="center" gap={0.5}>
									<span>{c}</span>
									{countPerCurrency[c] != null && (
										<Chip label={countPerCurrency[c]} size="small" sx={{ height: 18, fontSize: 11 }} />
									)}
								</Box>
							}
							sx={{ textTransform: 'none', minHeight: 40 }}
						/>
					))}
				</Tabs>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={200} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : freeByCurrency.length === 0 ? (
				<Alert severity="info">No hay recibos libres en {currency} para esta caja.</Alert>
			) : (
				<TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 420, overflow: 'auto' }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox
										size="small"
										checked={allSelected}
										indeterminate={selectedCountInView > 0 && !allSelected}
										onChange={toggleAll}
									/>
								</TableCell>
								<TableCell>Recibo</TableCell>
								<TableCell>Fecha</TableCell>
								<TableCell align="right">Monto ({currency})</TableCell>
								<TableCell>Observaciones</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{freeByCurrency.map((item) => (
								<TableRow
									key={item.id}
									hover
									onClick={() => toggleOne(item.id)}
									sx={{ cursor: 'pointer' }}
									selected={selectedIds.has(item.id)}
								>
									<TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
										<Checkbox size="small" checked={selectedIds.has(item.id)} onChange={() => toggleOne(item.id)} />
									</TableCell>
									<TableCell>{item.receiptNumber}</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.createdAt)}</TableCell>
									<TableCell align="right">
										<Typography
											variant="body2"
											fontWeight={600}
											color={Number(item.transactionAmount) < 0 ? 'error.main' : 'text.primary'}
										>
											{formatCashAmount(Number(item.transactionAmount), item.transactionCurrency)}
										</Typography>
									</TableCell>
									<TableCell sx={{ maxWidth: 180 }}>
										<Typography variant="body2" color="text.secondary" noWrap>
											{item.observations ?? '—'}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{hasMore && (
				<Box display="flex" justifyContent="center" mb={2}>
					<Chip
						label={loadingMore ? 'Cargando…' : `Cargar más (${freeByCurrency.length} de ${pagination?.total ?? 0})`}
						onClick={loadMore}
						variant="outlined"
						size="small"
						sx={{ cursor: 'pointer' }}
					/>
				</Box>
			)}

			{/* Resumen de selección */}
			{selectedCountInView > 0 && (
				<Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
					<Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
						<Typography variant="body2" fontWeight={600}>
							{selectedCountInView} recibo{selectedCountInView !== 1 ? 's' : ''} seleccionado
							{selectedCountInView !== 1 ? 's' : ''} en {currency}
						</Typography>
						<Chip label={formatCashAmount(total, currency)} size="small" color="primary" />
					</Box>
				</Paper>
			)}

			<Box display="flex" justifyContent="flex-end">
				<Button
					variant="contained"
					disabled={selectedCountInView === 0}
					onClick={handleContinuar}
					sx={{ textTransform: 'none' }}
				>
					Continuar →
				</Button>
			</Box>
		</>
	);
}

// ─── Paso 2: Ecuación y confirmación ─────────────────────────────────────────

interface Step2Props {
	branch: string;
	currency: string;
	selectedReceipts: IBranchReceiptItem[];
	onBack: () => void;
	onSuccess: (depositId: number) => void;
}

function Step2ConfirmDeposit({ branch, currency, selectedReceipts, onBack, onSuccess }: Step2Props): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const hasVault = VAULT_BRANCHES.includes(branch);

	const [depositedAmount, setDepositedAmount] = useState('');
	const [vaultAmountStr, setVaultAmountStr] = useState('');
	const [bankReference, setBankReference] = useState('');
	const [notes, setNotes] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [validationBreakdown, setValidationBreakdown] = useState<Record<string, number> | undefined>(undefined);

	// Preview del API: recibos + checklist de vueltos + saldo de la chica.
	// Mientras carga (o si falla) usamos el total de recibos del cliente.
	const [preview, setPreview] = useState<ICashDepositPreview | null>(null);
	const [previewLoading, setPreviewLoading] = useState(true);
	// Vueltos que este acta compensa (pre-seleccionados los que matchean por orden).
	const [selectedChangeIds, setSelectedChangeIds] = useState<Set<number>>(new Set());

	const receiptIdsKey = selectedReceipts.map((r) => r.id).join(',');
	useEffect(() => {
		let cancelled = false;
		setPreviewLoading(true);
		previewCashDeposit({ branch, currency, receiptIds: receiptIdsKey.split(',') })
			.then((data) => {
				if (cancelled) return;
				setPreview(data);
				setSelectedChangeIds(new Set(data.pendingChange.filter((c) => c.matchesActa).map((c) => c.id)));
			})
			.catch(() => {
				if (!cancelled) setPreview(null); // fallback silencioso: sin vueltos
			})
			.finally(() => {
				if (!cancelled) setPreviewLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [branch, currency, receiptIdsKey]);

	const toggleChange = (id: number) => {
		setSelectedChangeIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	// Parsea tolerando tanto formato es-AR (1.200.000,50) como raw (1200000.50)
	const parseAmount = (s: string): number => parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;

	const formatOnBlur = (s: string, set: (v: string) => void) => {
		const n = parseAmount(s);
		if (n > 0) set(new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(n));
	};

	const rawOnFocus = (s: string, set: (v: string) => void) => {
		const n = parseAmount(s);
		if (n > 0) set(String(n));
	};

	const receiptsTotal = preview?.receiptsTotal ?? totalInCurrency(selectedReceipts, currency);
	const pendingChange = preview?.pendingChange ?? [];
	// Vueltos elegidos = plata de la chica que viene dentro del sobre.
	const changeLent = round2(
		pendingChange.filter((c) => selectedChangeIds.has(c.id)).reduce((sum, c) => sum + c.amount, 0),
	);
	const cashOnHand = round2(receiptsTotal + changeLent);
	const pettyCashBalance = preview?.pettyCashBalance ?? null;

	const bankNum = parseAmount(depositedAmount);
	const vaultNum = hasVault ? parseAmount(vaultAmountStr) : 0;
	const allocated = round2(bankNum + vaultNum);
	// en mano + retiro de chica = banco + fuerte + resto a chica
	const pettyCashKept = allocated <= cashOnHand ? round2(cashOnHand - allocated) : 0;
	const pettyCashWithdrawn = allocated > cashOnHand ? round2(allocated - cashOnHand) : 0;
	const withdrawnExceedsBalance =
		pettyCashWithdrawn > 0 && pettyCashBalance !== null && pettyCashWithdrawn > pettyCashBalance;
	const equationOk = allocated > 0;

	const handleSubmit = async () => {
		if (!equationOk) return;
		setLoading(true);
		setError(null);
		setValidationBreakdown(undefined);

		try {
			const result = await postCashDeposit({
				branch,
				currency,
				receiptIds: selectedReceipts.map((r) => r.id),
				depositedAmount: bankNum,
				vaultAmount: vaultNum > 0 ? vaultNum : undefined,
				// Si el preview falló no mandamos selección: el API matchea automático.
				changeMovementIds: preview ? [...selectedChangeIds] : undefined,
				pettyCashKept: pettyCashKept > 0 ? pettyCashKept : undefined,
				pettyCashWithdrawn: pettyCashWithdrawn > 0 ? pettyCashWithdrawn : undefined,
				bankReference: bankReference.trim() || undefined,
				notes: notes.trim() || undefined,
				registeredBy: user?.email ?? 'unknown',
			});
			onSuccess(result.id);
		} catch (err) {
			const valErr = extractDepositValidationError(err);
			if (valErr) {
				setError(valErr.message);
				setValidationBreakdown(valErr.breakdown);
			} else {
				setError(extractApiError(err));
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			{/* Resumen de recibos seleccionados */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Stack direction="row" alignItems="baseline" gap={1} mb={1}>
					<Typography variant="subtitle2" fontWeight={600}>
						{selectedReceipts.length} recibo{selectedReceipts.length !== 1 ? 's' : ''} seleccionado
						{selectedReceipts.length !== 1 ? 's' : ''}
					</Typography>
					<Chip label={currency} size="small" color="primary" variant="outlined" />
				</Stack>
				<Typography variant="h6" fontWeight={700}>
					{formatCashAmount(receiptsTotal, currency)}
				</Typography>
			</Paper>

			{/* Vueltos a compensar */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Box display="inline-flex" alignItems="center" gap={0.5} mb={1}>
					<Typography variant="subtitle2" fontWeight={600}>
						Vueltos de caja chica que compensa este depósito
					</Typography>
					<SectionInfoTooltip content={changeChecklistTooltip} />
				</Box>
				{previewLoading ? (
					<Skeleton variant="rectangular" height={60} />
				) : pendingChange.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No hay vueltos pendientes en {currency} — el sobre debería tener exactamente el total de los recibos.
					</Typography>
				) : (
					<>
						<Typography variant="caption" color="text.secondary" display="block" mb={1}>
							Marcá los vueltos que diste de la caja chica y cuyo efectivo viene dentro de este sobre. Los de las
							órdenes de estos recibos ya vienen marcados.
						</Typography>
						<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox" />
										<TableCell>Fecha</TableCell>
										<TableCell align="right">Monto</TableCell>
										<TableCell>Orden</TableCell>
										<TableCell>Nota</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{pendingChange.map((c) => (
										<TableRow
											key={c.id}
											hover
											onClick={() => toggleChange(c.id)}
											sx={{ cursor: 'pointer' }}
											selected={selectedChangeIds.has(c.id)}
										>
											<TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
												<Checkbox
													size="small"
													checked={selectedChangeIds.has(c.id)}
													onChange={() => toggleChange(c.id)}
												/>
											</TableCell>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.createdAt)}</TableCell>
											<TableCell align="right">
												<Typography variant="body2" fontWeight={600}>
													{formatCashAmount(c.amount, currency)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color={c.matchesActa ? 'text.primary' : 'text.secondary'}>
													{c.saleOrderId ?? '—'}
												</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 180 }}>
												<Typography variant="body2" color="text.secondary" noWrap title={c.note ?? ''}>
													{c.note ?? '—'}
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						{selectedChangeIds.size > 0 && (
							<Box mt={1}>
								<Chip
									label={`${selectedChangeIds.size} vuelto${
										selectedChangeIds.size !== 1 ? 's' : ''
									} = ${formatCashAmount(changeLent, currency)}`}
									size="small"
									color="info"
									variant="outlined"
								/>
							</Box>
						)}
					</>
				)}
			</Paper>

			{/* Ecuación / reparto */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Box display="inline-flex" alignItems="center" gap={0.5} mb={2}>
					<Typography variant="subtitle2" fontWeight={600}>
						¿A dónde va el efectivo?
					</Typography>
					<SectionInfoTooltip content={equationTooltip} />
				</Box>

				<Stack spacing={2}>
					<Box display="flex" gap={3} flexWrap="wrap">
						<Box>
							<Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
								Total de recibos en {currency}
							</Typography>
							<Typography variant="h6" fontWeight={700}>
								{formatCashAmount(receiptsTotal, currency)}
							</Typography>
						</Box>
						{changeLent > 0 && (
							<Box>
								<Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
									+ Vueltos compensados
								</Typography>
								<Typography variant="h6" fontWeight={700} color="info.main">
									{formatCashAmount(changeLent, currency)}
								</Typography>
							</Box>
						)}
						<Box>
							<Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
								= Efectivo en mano
							</Typography>
							<Typography variant="h6" fontWeight={700} color="primary.main">
								{formatCashAmount(cashOnHand, currency)}
							</Typography>
						</Box>
					</Box>

					<Divider />

					<TextField
						label={`Monto que llevás al banco (${currency})`}
						size="small"
						fullWidth
						value={depositedAmount}
						onChange={(e) => {
							setDepositedAmount(e.target.value);
							setError(null);
							setValidationBreakdown(undefined);
						}}
						onFocus={() => rawOnFocus(depositedAmount, setDepositedAmount)}
						onBlur={() => formatOnBlur(depositedAmount, setDepositedAmount)}
						placeholder="0"
						InputProps={{
							endAdornment: (
								<Box display="flex" alignItems="center" gap={0.5}>
									<AccountBalanceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
									<Typography variant="caption" color="text.secondary">
										{currency}
									</Typography>
								</Box>
							),
						}}
						helperText={`El monto que físicamente estás llevando en el sobre. El efectivo en mano es ${formatCashAmount(
							cashOnHand,
							currency,
						)}; la diferencia queda en caja chica o sale de ella.`}
					/>

					{hasVault && (
						<TextField
							label={`Monto que entra a la caja fuerte (${currency})`}
							size="small"
							fullWidth
							value={vaultAmountStr}
							onChange={(e) => {
								setVaultAmountStr(e.target.value);
								setError(null);
								setValidationBreakdown(undefined);
							}}
							onFocus={() => rawOnFocus(vaultAmountStr, setVaultAmountStr)}
							onBlur={() => formatOnBlur(vaultAmountStr, setVaultAmountStr)}
							placeholder="0"
							InputProps={{
								endAdornment: (
									<Box display="flex" alignItems="center" gap={0.5}>
										<LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
										<Typography variant="caption" color="text.secondary">
											{currency}
										</Typography>
									</Box>
								),
							}}
							helperText="Billetes que el banco no aceptó o efectivo que se guarda en la caja fuerte de la sucursal (dejalo en 0 si todo va al banco)."
						/>
					)}

					{allocated > 0 && pettyCashKept > 0 && (
						<Box
							display="flex"
							alignItems="center"
							gap={1}
							sx={{ p: 1.5, borderRadius: 1, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}
						>
							<Box flex={1}>
								<Typography variant="caption" color="text.secondary">
									Resto que queda en caja chica ({currency})
								</Typography>
								<Typography variant="h6" fontWeight={700} color="success.main">
									{formatCashAmount(pettyCashKept, currency)}
								</Typography>
							</Box>
							<CheckCircleIcon color="success" />
						</Box>
					)}

					{allocated > 0 && pettyCashWithdrawn > 0 && (
						<Box
							display="flex"
							alignItems="center"
							gap={1}
							sx={{
								p: 1.5,
								borderRadius: 1,
								bgcolor: withdrawnExceedsBalance ? 'error.50' : 'warning.50',
								border: 1,
								borderColor: withdrawnExceedsBalance ? 'error.200' : 'warning.200',
							}}
						>
							<Box flex={1}>
								<Typography variant="caption" color="text.secondary">
									Retiro extra de caja chica ({currency})
								</Typography>
								<Typography
									variant="h6"
									fontWeight={700}
									color={withdrawnExceedsBalance ? 'error.main' : 'warning.main'}
								>
									{formatCashAmount(pettyCashWithdrawn, currency)}
								</Typography>
								{pettyCashBalance !== null && (
									<Typography variant="caption" color="text.secondary">
										Saldo actual de la chica: {formatCashAmount(pettyCashBalance, currency)}
									</Typography>
								)}
							</Box>
						</Box>
					)}

					{allocated > 0 && pettyCashKept === 0 && pettyCashWithdrawn === 0 && (
						<Box
							display="flex"
							alignItems="center"
							gap={1}
							sx={{ p: 1.5, borderRadius: 1, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}
						>
							<Typography variant="body2" color="success.main" fontWeight={600} flex={1}>
								Llevás exactamente el efectivo en mano — no queda resto ni salís de la caja chica.
							</Typography>
							<CheckCircleIcon color="success" />
						</Box>
					)}

					{withdrawnExceedsBalance && (
						<Alert severity="warning">
							El retiro extra ({formatCashAmount(pettyCashWithdrawn, currency)}) supera el saldo actual de la caja chica
							({formatCashAmount(pettyCashBalance ?? 0, currency)}) — la chica quedaría en negativo. Verificá el conteo
							antes de continuar.
						</Alert>
					)}
				</Stack>
			</Paper>

			{/* Datos del depósito — se completan DESPUÉS de ir al banco */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Typography variant="subtitle2" fontWeight={600} gutterBottom>
					Datos del comprobante
				</Typography>
				<Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
					Estos datos los completás <strong>cuando volvés del banco</strong> con el comprobante en mano. Podés dejarlo
					en blanco ahora y completarlo al confirmar el acta.
				</Alert>
				<Stack spacing={2}>
					<TextField
						label="Referencia bancaria"
						size="small"
						fullWidth
						value={bankReference}
						onChange={(e) => setBankReference(e.target.value)}
						placeholder="Nro. de comprobante, ticket, etc."
					/>
					<TextField
						label="Notas internas"
						size="small"
						fullWidth
						multiline
						rows={2}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Cualquier aclaración para administración"
					/>
				</Stack>
			</Paper>

			{/* Error de validación con breakdown */}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
					{validationBreakdown && (
						<Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
							{Object.entries(validationBreakdown).map(([key, val]) => (
								<li key={key}>
									<Typography variant="caption">
										{key}: {typeof val === 'number' ? formatCashAmount(val, currency) : String(val)}
									</Typography>
								</li>
							))}
						</Box>
					)}
				</Alert>
			)}

			<Box display="flex" justifyContent="space-between">
				<Button variant="outlined" onClick={onBack} disabled={loading} sx={{ textTransform: 'none' }}>
					← Volver
				</Button>
				<Button
					variant="contained"
					disabled={!equationOk || loading}
					onClick={handleSubmit}
					startIcon={loading ? <CircularProgress size={16} /> : <AccountBalanceIcon />}
					sx={{ textTransform: 'none' }}
				>
					Crear borrador de acta
				</Button>
			</Box>
		</>
	);
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface DepositWizardProps {
	branch: string;
}

const STEPS = ['Seleccionar recibos', 'Confirmar depósito'];

/**
 * Wizard de dos pasos para crear un borrador de acta de depósito.
 *
 * Paso 1: elegir recibos por moneda (tabs si hay más de una). Cada acta es por una
 * sola moneda; si hay recibos en múltiples monedas se hacen múltiples depósitos.
 *
 * Paso 2: ingresar monto al banco, ver ecuación, completar datos del comprobante.
 * Los datos bancarios se completan al volver del banco; pueden dejarse en blanco.
 */
export function DepositWizard({ branch }: DepositWizardProps): JSX.Element {
	const history = useHistory();
	const [activeStep, setActiveStep] = useState(0);
	const [step1Result, setStep1Result] = useState<Step1Result | null>(null);

	const handleNext = (result: Step1Result) => {
		setStep1Result(result);
		setActiveStep(1);
	};

	const handleSuccess = (depositId: number) => {
		history.push(routes.branchCash.depositDetail(depositId));
	};

	return (
		<>
			<Stepper activeStep={activeStep} sx={{ mb: 4 }}>
				{STEPS.map((label) => (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
				))}
			</Stepper>

			{activeStep === 0 ? (
				<Step1SelectReceipts branch={branch} onNext={handleNext} />
			) : step1Result ? (
				<Step2ConfirmDeposit
					branch={branch}
					currency={step1Result.currency}
					selectedReceipts={step1Result.receipts}
					onBack={() => setActiveStep(0)}
					onSuccess={handleSuccess}
				/>
			) : null}
		</>
	);
}
